import React from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { CalendarFilledIcon, DollarCircleIcon, LikeIcon } from 'icons';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { globalConst, useInteractionHandler, UserInteraction } from 'modules/global';

import { Visible } from 'components';

import { IAllUniversities, UniversityTypes } from '../../universities/models';

interface IProps {
  data: IAllUniversities;
  index?: number;
}

const UniversitiesCard: React.FC<IProps> = ({ data, index }) => {
  const t = useTranslations();
  const { onInteractionClick } = useInteractionHandler(data?.id);

  return (
    <>
      <Link
        href={`${data?.type === UniversityTypes.PRIVATE ? globalConst.pageNames.PRIVATE_UNIVERSITIES : globalConst.pageNames.UNIVERSITIES}/${data?.slug}`}
        className="h-full cursor-pointer rounded-xl bg-white"
        onClick={onInteractionClick(UserInteraction.CARD_CLICK)}
      >
        <div className="flex h-full flex-col rounded-xl bg-white p-3 hover:text-green-500">
          {data?.image ? (
            <div className="max-h-29.5 min-h-29.5 relative w-full overflow-hidden rounded-lg">
              {data?.is_recommended && (
                <div className="absolute -right-3 z-10 flex items-center gap-1 rounded-full bg-orange-500 pl-2 pr-5 text-xs text-white">
                  <LikeIcon /> {t('tavsiya_etiladi')}
                </div>
              )}
              <Image
                fill
                priority={!index}
                className="rounded-lg object-cover"
                src={data?.image?.trimEnd() || '/images/default-no-image.webp'}
                alt={data?.name}
                title="Universitet rasmi"
              />
            </div>
          ) : (
            <div className="max-h-29.5 min-h-29.5 relative flex w-full items-center justify-center overflow-hidden rounded-lg rounded-lg bg-gray-50">
              {data?.is_recommended && (
                <div className="absolute -right-3 top-0 z-10 flex items-center gap-1 rounded-full bg-orange-500 pl-2 pr-5 text-xs text-white">
                  <LikeIcon /> {t('tavsiya_etiladi')}
                </div>
              )}
              <Image
                width={70}
                height={70}
                className=" object-cover"
                src="/images/default-no-image.webp"
                alt={data?.name}
                title="Universitet rasmi"
              />
            </div>
          )}

          <div className="relative flex h-full flex-1 flex-col justify-between">
            <div>
              <div className="absolute -top-5 right-3">
                <div className="relative flex max-h-9 min-h-9 min-w-9 max-w-9 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-50">
                  <Image
                    src={data?.logo || '/images/default-no-logo.webp'}
                    alt={data?.name?.slice(0, 1)}
                    width={data?.logo ? 38 : 18}
                    height={data?.logo ? 38 : 18}
                    className="object-contain"
                    title="Universitet logosi"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-3">
                <div className="line-clamp-2 text-xs font-medium">{data?.name}</div>
              </div>
            </div>

            <div>
              <div className="my-2 flex items-center justify-between">
                <span className="text-[0.625rem] text-indigo-600">
                  {t('Yonalishlar')} - {data?.direction_count}
                </span>
                <span
                  className={classNames('rounded-full px-2 py-1 text-[0.625rem]', {
                    'bg-teal-50 text-teal-500': data?.type === UniversityTypes.PUBLIC,
                    'bg-orange-50 text-orange-500': data?.type === UniversityTypes.PRIVATE
                  })}
                >
                  <Visible
                    if={data?.type === UniversityTypes.PUBLIC}
                    else={<Visible if={data?.type === UniversityTypes.PRIVATE}>{t('xususiy')}</Visible>}
                  >
                    {t('davlat')}
                  </Visible>
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-xl bg-gray-50 px-2 py-1.5">
                  <div className="text-blue-500">
                    <DollarCircleIcon />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[0.5rem] text-gray-600">{t('kontrakt_tolovi')}</span>
                    <span className="text-[0.625rem] font-medium text-gray-900">
                      {data?.min_contract_price || data?.max_contract_price ? (
                        <>
                          {Number(data?.min_contract_price).toLocaleString('ru')} -{' '}
                          {Number(data?.max_contract_price).toLocaleString('ru')} UZS
                        </>
                      ) : (
                        t('mavjud_emas')
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-yellow-50 px-2 py-1.5">
                  <div className="text-yellow-400">
                    <CalendarFilledIcon />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[0.5rem] text-gray-600">{t('qabul_muddati')}</span>
                    <span className="text-[0.625rem] font-medium text-gray-900">
                      {data?.min_contract_price || data?.max_contract_price ? (
                        <>
                          {dayjs(Number(data?.admission_start_date) * 1000).format('DD.MM.YYYY')} -{' '}
                          {dayjs(Number(data?.admission_end_date) * 1000).format('DD.MM.YYYY')}
                        </>
                      ) : (
                        t('mavjud_emas')
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </>
  );
};

export default UniversitiesCard;
